import { Box, IconButton, List, ListItemButton, Tooltip } from "@mui/material";
import {
  TCustomCheckoutComponentItem,
  TCustomCheckoutComponentType,
  isDraggableItem,
  isDeletableItem,
  isEmptyItem,
  isNoneItem,
  isContainerItem,
} from "../../core/types/CustomCheckout";
import CheckoutComponenetWidget from "./CheckoutComponentWidget";
import { useRef } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckoutFunctions from "../../core/functions/CheckoutFunctions";
import { newCustomCheckoutEmpty } from "../../core/constants";
import NumberFunctions from "../../core/functions/NumberFunctions";
import { useCheckoutBuilder } from "../../core/context/CheckoutBuilderContext";

export interface ICheckoutComponenetListProps {
  /** Override da lista quando usado recursivamente (ex: dentro de Layout2_3) */
  listOverride?: TCustomCheckoutComponentItem[];
  /** Override do setList para listas aninhadas */
  setListOverride?: (value: TCustomCheckoutComponentItem[]) => void;
}

const CheckoutComponenetList = ({
  listOverride,
  setListOverride,
}: ICheckoutComponenetListProps = {}) => {
  const {
    list: contextList,
    dragOverItem,
    dragOverItemOld,
    dropZoneTarget,
    mouseEnterItemId,
    setList: contextSetList,
    onEdit,
    onDelete,
    setMouseEnterItemId,
    onDropItem,
  } = useCheckoutBuilder();

  // Usa override se fornecido (caso recursivo), senão usa o context
  const list = listOverride ?? contextList;
  const setList = setListOverride ?? contextSetList;
  const parentList = contextList;

  const dragItem = useRef("");

  const dragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    // PROTEÇÃO: Não permitir arrastar o componente checkout
    if (!isDraggableItem(list[position])) {
      e.preventDefault();
      return;
    }
    dragItem.current = list[position].id;
    // Não propaga para listas pai
    e.stopPropagation();
  };

  const dragEnter = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    e.preventDefault();
    // CRÍTICO: stopPropagation impede que o evento suba para a lista pai,
    // que sobrescreveria dragOverItem.current com o ID do container (ex: Layout2_3)
    e.stopPropagation();

    // Se o alvo é um empty (placeholder), NÃO atualizar dragOverItem.
    // Mantém apontando para o componente anterior, permitindo que o drop funcione
    // tanto ao soltar no placeholder quanto no componente vizinho.
    if (isEmptyItem(list[position])) {
      return;
    }

    dragOverItemOld.current = dragOverItem.current;
    dragOverItem.current = list[position].id;
    // Limpa zona de drop fixa ao entrar em um componente real
    dropZoneTarget.current = "";

    // Para containers (list): apenas marca o dragOverItem, NÃO manipula a lista.
    // O setList causa re-render que recria o DOM e impede que handlers internos
    // (handleLateralDragEnter) disparem. O drop é tratado por insertIntoContainer.
    // NOTA: layout2_3 NÃO é pulado aqui — precisa do placeholder para permitir
    // inserir componentes ANTES/DEPOIS dele na lista raiz.
    if (isContainerItem(list[position])) {
      return;
    }

    // Limpa empties da lista antes de manipular
    let copyList = CheckoutFunctions.clearEmpty(list);

    // Busca o item ATUAL (onde o mouse está agora), não o antigo
    const { parent, components, component, index } = CheckoutFunctions.getComponent(
      undefined,
      copyList,
      dragOverItem.current,
    );

    // Se não encontrou o componente alvo, não faz nada
    if (!component || isEmptyItem(component)) {
      return;
    }

    if (components) {
      copyList = components.filter((item) => item.type !== TCustomCheckoutComponentType.empty);
    }

    copyList.splice(index, 0, {
      ...newCustomCheckoutEmpty,
      id: NumberFunctions.getRandomInt(99999).toString(),
    });

    let order = 0;
    copyList.forEach((item) => {
      order += 1;
      item.order = order;
    });

    copyList = copyList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (!parent) {
      setList([...copyList]);
    } else {
      let newList = CheckoutFunctions.clearEmpty(list);
      newList = newList.map((l) =>
        l.id === parent.id
          ? { ...parent, children: copyList }
          : {
              ...l,
              children: l.children.map((c) =>
                c.id === parent.id ? { ...parent, children: copyList } : { ...c },
              ),
            },
      );
      setList(newList);
    }
  };

  const mouseEnter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, position: number) => {
    if (mouseEnterItemId !== list[position].id) {
      setMouseEnterItemId(list[position].id);
    }
  };

  const mouseLeave = (_e: React.MouseEvent<HTMLDivElement, MouseEvent>, _position: number) => {
    setMouseEnterItemId("");
  };

  return (
    <Box>
      <List disablePadding>
        {list
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((item, index) => {
            const isHovered = mouseEnterItemId === item.id;
            const showActions = isHovered && !isEmptyItem(item);

            return (
              <ListItemButton
                draggable
                disableGutters
                disableRipple
                disableTouchRipple
                key={item.id}
                onDragStart={(e) => dragStart(e, index)}
                onDragEnter={(e) => dragEnter(e, index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={(e) => {
                  e.stopPropagation();
                  if (dragOverItem.current === list[index].id) {
                    dragOverItem.current = dragOverItemOld.current;
                  }
                }}
                onDragEnd={(e) => {
                  e.stopPropagation();
                  onDropItem(e, dragItem);
                }}
                onMouseEnter={(e) => mouseEnter(e, index)}
                onMouseLeave={(e) => mouseLeave(e, index)}
                sx={{
                  p: 0.5,
                  m: 0,
                  borderRadius: "6px",
                  border: "2px solid transparent",
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                  position: "relative",
                  ...(isHovered &&
                    !isEmptyItem(item) && {
                      borderColor: "#1976d2",
                      backgroundColor: "rgba(25, 118, 210, 0.02)",
                    }),
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <CheckoutComponenetWidget item={item} parentList={parentList} />

                {showActions && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: 4,
                      top: 4,
                      backgroundColor: "white",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      overflow: "hidden",
                      display: "flex",
                      zIndex: 10,
                    }}
                  >
                    {!isNoneItem(item) && (
                      <Tooltip title="Editar" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={(e) => onEdit(e, item)}
                          sx={{
                            borderRadius: 0,
                            px: 1,
                            py: 0.5,
                            color: "#1976d2",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.08)",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isDeletableItem(item) && (
                      <Tooltip title="Excluir" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(item)}
                          sx={{
                            borderRadius: 0,
                            px: 1,
                            py: 0.5,
                            color: "#d32f2f",
                            "&:hover": {
                              backgroundColor: "rgba(211, 47, 47, 0.08)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </ListItemButton>
            );
          })}
      </List>
    </Box>
  );
};

export default CheckoutComponenetList;
